
import { useRef, useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Eraser, Type, PenLine } from "lucide-react";

interface SignatureBoxProps {
  onChange: (signature: string | null) => void;
}

export const SignatureBox = ({ onChange }: SignatureBoxProps) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [signatureMode, setSignatureMode] = useState<"draw" | "type">("draw");
  const [typedName, setTypedName] = useState("");

  // Add effect to listen for reset events
  useEffect(() => {
    const handleReset = () => {
      clearSignature();
    };
    
    // Listen for custom reset event
    document.addEventListener('resetSignature', handleReset);
    
    // Cleanup listener on unmount
    return () => {
      document.removeEventListener('resetSignature', handleReset);
    };
  }, []);

  const startDrawing = (e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    setIsDrawing(true);
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const rect = canvas.getBoundingClientRect();
    let x, y;

    if ('touches' in e) {
      x = e.touches[0].clientX - rect.left;
      y = e.touches[0].clientY - rect.top;
    } else {
      x = e.clientX - rect.left;
      y = e.clientY - rect.top;
    }

    ctx.beginPath();
    ctx.moveTo(x, y);
  };

  const draw = (e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>) => {
    if (!isDrawing) return;

    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const rect = canvas.getBoundingClientRect();
    let x, y;

    if ('touches' in e) {
      x = e.touches[0].clientX - rect.left;
      y = e.touches[0].clientY - rect.top;
    } else {
      x = e.clientX - rect.left;
      y = e.clientY - rect.top;
    }

    ctx.lineWidth = 3; // Slightly thicker line for better visibility
    ctx.lineCap = 'round';
    ctx.strokeStyle = '#000'; // Ensure black color for clarity
    ctx.lineTo(x, y);
    ctx.stroke();
  };

  const stopDrawing = () => {
    setIsDrawing(false);
    const canvas = canvasRef.current;
    if (!canvas) return;

    const signature = canvas.toDataURL();
    onChange(signature);
  };

  const clearSignature = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    ctx.clearRect(0, 0, canvas.width, canvas.height);
    onChange(null);
    setTypedName("");
  };

  const handleTypeSignature = (e: React.ChangeEvent<HTMLInputElement>) => {
    setTypedName(e.target.value);
    
    // Generate typed signature
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    
    // Clear the canvas first
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    
    // Set up the font
    ctx.font = '36px "Times New Roman", serif';
    ctx.fillStyle = '#000';
    
    // Position text in the middle
    const textWidth = ctx.measureText(e.target.value).width;
    const x = (canvas.width - textWidth) / 2;
    const y = canvas.height / 2;
    
    // Draw the signature
    ctx.fillText(e.target.value, x, y);
    
    // Convert to data URL and update
    const signature = canvas.toDataURL();
    onChange(signature);
  };

  const toggleSignatureMode = () => {
    clearSignature(); // Clear when switching modes
    setSignatureMode(prev => prev === "draw" ? "type" : "draw");
  };

  return (
    <div className="space-y-2">
      <div className="bg-white border rounded-md">
        {signatureMode === "draw" ? (
          <div className="p-4">
            <p className="text-sm text-muted-foreground mb-2">Draw your signature below:</p>
            <div className="border border-gray-200 rounded-md bg-slate-50 relative">
              <canvas
                ref={canvasRef}
                width={400}
                height={150}
                className="w-full touch-none"
                onMouseDown={startDrawing}
                onMouseMove={draw}
                onMouseUp={stopDrawing}
                onMouseLeave={stopDrawing}
                onTouchStart={startDrawing}
                onTouchMove={draw}
                onTouchEnd={stopDrawing}
              />
              {/* Signature line */}
              <div className="absolute bottom-10 left-4 right-4 border-b border-gray-400"></div>
            </div>
          </div>
        ) : (
          <div className="p-4">
            <p className="text-sm text-muted-foreground mb-2">Type your signature below:</p>
            <Input
              type="text"
              value={typedName}
              onChange={handleTypeSignature}
              placeholder="Type your full name"
              className="font-serif text-lg"
            />
            <div className="mt-3 border border-gray-200 rounded-md bg-slate-50 h-[150px] flex items-center justify-center">
              <canvas
                ref={canvasRef}
                width={400}
                height={150}
                className="w-full h-full"
              />
            </div>
          </div>
        )}
      </div>
      <div className="flex gap-2">
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={clearSignature}
          className="flex-1"
        >
          <Eraser className="w-4 h-4 mr-2" />
          Clear
        </Button>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={toggleSignatureMode}
          className="flex-1"
        >
          {signatureMode === "draw" ? (
            <>
              <Type className="w-4 h-4 mr-2" />
              Type Instead
            </>
          ) : (
            <>
              <PenLine className="w-4 h-4 mr-2" />
              Draw Instead
            </>
          )}
        </Button>
      </div>
    </div>
  );
};
